import { useCallback, useEffect, useRef } from 'react';
import { fetchQuery, useMutation, useRelayEnvironment } from 'react-relay';
import type { ExperimentsGetSpeculativeDataQuery } from '../graphQLOperations/__generated__/ExperimentsGetSpeculativeDataQuery.graphql';
import type { ExperimentsRecordExperimentsSpeculativeDataMutation } from '../graphQLOperations/__generated__/ExperimentsRecordExperimentsSpeculativeDataMutation.graphql';
import { getExperimentSpeculativeData, recordExperimentSpeculativeData } from '../graphQLOperations/Experiments';
import { useAppSelector } from '../redux/hooks';
import { selectUser } from '../redux/reducers/userSlice';
import { BRAIN_BREAK_FLAG_NAME } from '../models/BrainBreak';
import {
  buildLockPayload,
  ACTIVE_MODAL_SESSION_VARIANT,
  extractLockData,
  isLockHeldByAnotherSession,
  tryAcquireLocalLock,
  isLocalLockHeld,
  releaseLocalLock,
  isLockOwnedBySession,
  getHeartbeatDelay,
} from '../businessLogicService/brainBreakSessionLock';

export function useBrainBreakSessionLock() {
  const [commitSpeculativeData] = useMutation<ExperimentsRecordExperimentsSpeculativeDataMutation>(
    recordExperimentSpeculativeData,
  );
  const user = useAppSelector(selectUser);
  const relayEnvironment = useRelayEnvironment();

  const tabSessionIdRef = useRef(crypto.randomUUID());
  const heartbeatTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isHoldingLockRef = useRef(false);
  const renewalInFlightRef = useRef(false);
  const lockAcquiredAtRef = useRef<number | null>(null);

  const clearHeartbeat = useCallback(() => {
    if (heartbeatTimeoutRef.current) {
      clearTimeout(heartbeatTimeoutRef.current);
      heartbeatTimeoutRef.current = null;
    }
  }, []);

  const getUserId = useCallback((): string | undefined => {
    return user?.id ? `UserNode:${user.id}` : undefined;
  }, [user?.id]);

  const getPublicUserId = useCallback((): string | undefined => {
    return user?.sub;
  }, [user?.sub]);

  const upsertLock = useCallback(
    (sessionId: string): Promise<boolean> => {
      return new Promise(resolve => {
        commitSpeculativeData({
          variables: {
            input: {
              input: {
                flagName: BRAIN_BREAK_FLAG_NAME,
                variantName: ACTIVE_MODAL_SESSION_VARIANT,
                payload: buildLockPayload(sessionId),
              },
            },
          },
          onCompleted: () => resolve(true),
          onError: () => resolve(false),
        });
      });
    },
    [commitSpeculativeData, getUserId],
  );

  const fetchCurrentLock = useCallback(async () => {
    const publicUserId = getPublicUserId();
    if (!publicUserId) return undefined;

    const freshData = await fetchQuery<ExperimentsGetSpeculativeDataQuery>(
      relayEnvironment,
      getExperimentSpeculativeData,
      { userId: publicUserId },
      { fetchPolicy: 'network-only' },
    ).toPromise();

    return extractLockData(freshData ?? null);
  }, [getPublicUserId, relayEnvironment]);

  const syncServerLock = useCallback(
    async (sessionId: string): Promise<boolean> => {
      if (!getPublicUserId()) {
        return false;
      }

      try {
        const currentLock = await fetchCurrentLock();
        if (isLockHeldByAnotherSession(currentLock, sessionId)) {
          return false;
        }

        const success = await upsertLock(sessionId);
        if (!success) {
          return false;
        }

        const refreshedLock = await fetchCurrentLock();
        return isLockOwnedBySession(refreshedLock, sessionId);
      } catch {
        return false;
      }
    },
    [fetchCurrentLock, getPublicUserId, upsertLock],
  );

  const renewLock = useCallback(
    async (sessionId: string): Promise<boolean> => {
      if (!tryAcquireLocalLock(sessionId)) {
        return false;
      }

      return syncServerLock(sessionId);
    },
    [syncServerLock],
  );

  // Refs to avoid stale closures in setTimeout — always read latest version
  const renewLockRef = useRef(renewLock);
  renewLockRef.current = renewLock;

  const startHeartbeat = useCallback(
    (sessionId: string) => {
      clearHeartbeat();
      const acquiredAt = lockAcquiredAtRef.current ?? Date.now();
      const elapsed = Date.now() - acquiredAt;
      const delay = getHeartbeatDelay(elapsed);
      heartbeatTimeoutRef.current = setTimeout(async () => {
        if (document.visibilityState === 'visible' && isHoldingLockRef.current && !renewalInFlightRef.current) {
          renewalInFlightRef.current = true;
          const success = await renewLockRef.current(sessionId);
          renewalInFlightRef.current = false;
          if (success) {
            startHeartbeatRef.current(sessionId);
          } else {
            isHoldingLockRef.current = false;
            releaseLocalLock(sessionId);
          }
        }
      }, delay);
    },
    [clearHeartbeat],
  );

  const startHeartbeatRef = useRef(startHeartbeat);
  startHeartbeatRef.current = startHeartbeat;

  const acquireLock = useCallback(async (): Promise<boolean> => {
    const sessionId = tabSessionIdRef.current;

    if (!tryAcquireLocalLock(sessionId)) {
      return false;
    }

    const success = await syncServerLock(sessionId);
    if (!success) {
      releaseLocalLock(sessionId);
      return false;
    }

    isHoldingLockRef.current = true;
    lockAcquiredAtRef.current = Date.now();
    startHeartbeat(sessionId);
    return true;
  }, [startHeartbeat, syncServerLock]);

  const releaseLock = useCallback(() => {
    clearHeartbeat();
    releaseLocalLock(tabSessionIdRef.current);
    isHoldingLockRef.current = false;
    lockAcquiredAtRef.current = null;
  }, [clearHeartbeat]);

  const tryLocalLock = useCallback((): boolean => {
    return !isLocalLockHeld(tabSessionIdRef.current);
  }, []);

  // Re-validate server lock when tab becomes visible — guards against concurrent renewal
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isHoldingLockRef.current && !renewalInFlightRef.current) {
        clearHeartbeat();
        renewalInFlightRef.current = true;
        void renewLockRef.current(tabSessionIdRef.current).then(success => {
          renewalInFlightRef.current = false;
          if (!success) {
            isHoldingLockRef.current = false;
            releaseLocalLock(tabSessionIdRef.current);
            return;
          }

          startHeartbeatRef.current(tabSessionIdRef.current);
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [clearHeartbeat]);

  // Release lock on tab close - mutation may not complete but TTL handles expiry
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (isHoldingLockRef.current) {
        isHoldingLockRef.current = false;
        clearHeartbeat();
        releaseLocalLock(tabSessionIdRef.current);
        const userId = getUserId();
        if (userId) {
          commitSpeculativeData({
            variables: {
              input: {
                input: {
                  flagName: BRAIN_BREAK_FLAG_NAME,
                  variantName: ACTIVE_MODAL_SESSION_VARIANT,
                  payload: JSON.stringify({ sessionId: tabSessionIdRef.current, claimedAt: '' }),
                },
              },
            },
          });
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [commitSpeculativeData, clearHeartbeat, getUserId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearHeartbeat();
      releaseLocalLock(tabSessionIdRef.current);
      isHoldingLockRef.current = false;
    };
  }, [clearHeartbeat]);

  return { acquireLock, releaseLock, tryLocalLock };
}
